import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import CriteriaSorter from "./CriteriaSorter";
import OverallScoreSection from "./OverallScoreSection";
import { flattenCriteria } from "./flattenCriteria";
import { scrollIntoView } from "../helperFunctions";

export default function MultiStepRatingForm({
    result,
    noteHandle,
    scoreHandle,
    setRecordBoxID,
    fileSelectorHandle,
    overalScoreHandle,
    textBoxID,
    setTextBoxID,
    criteriaStateHandle,
    voiceFileHandle,
    onFinish,
}) {
    const { t } = useTranslation();
    const [stepIndex, setStepIndex] = useState(0);
    const progressRef = useRef(null);

    const criteriaList = useMemo(
        () =>
            [...(result.ajax.form.criteria_list || [])].sort(
                (a, b) => a.weight - b.weight
            ),
        [result.ajax.form.criteria_list]
    );

    const flatCriteria = useMemo(
        () => flattenCriteria(criteriaList),
        [criteriaList]
    );

    const totalSteps = 1 + flatCriteria.length;
    const isOverallStep = stepIndex === 0;
    const currentCriteria = flatCriteria[stepIndex - 1];
    const isLastStep = stepIndex === totalSteps - 1;

    const goToStep = (nextStep) => {
        setStepIndex(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleNext = () => {
        goToStep(stepIndex + 1);
    };

    const handlePrevious = () => {
        if (stepIndex > 0) {
            goToStep(stepIndex - 1);
        }
    };

    const handleFinish = () => {
        onFinish();
        setTimeout(() => {
            scrollIntoView("#final-form-title", 240);
        }, 400);
    };

    useEffect(() => {
        const notifyChrome = () => {
            window.dispatchEvent(new Event("feedback-chrome-reflow"));
        };
        notifyChrome();
        const frame = window.requestAnimationFrame(notifyChrome);
        return () => {
            window.cancelAnimationFrame(frame);
            notifyChrome();
        };
    }, [stepIndex]);

    return (
        <div className="rating rating-step-form">
            <div className="rating-step-progress-slot">
                <div className="rating-step-progress" ref={progressRef}>
                    <span className="rating-step-progress-count">
                        {t("feedback.form.step.progress", {
                            current: stepIndex + 1,
                            total: totalSteps,
                        })}
                    </span>
                    <div className="rating-step-progress-bar">
                        <div
                            className="rating-step-progress-fill"
                            style={{
                                width: `${((stepIndex + 1) / totalSteps) * 100}%`,
                            }}
                        />
                    </div>
                </div>
            </div>

            {isOverallStep ? (
                <OverallScoreSection
                    result={result}
                    noteHandle={noteHandle}
                    scoreHandle={scoreHandle}
                    setRecordBoxID={setRecordBoxID}
                    fileSelectorHandle={fileSelectorHandle}
                    overalScoreHandle={overalScoreHandle}
                />
            ) : (
                <CriteriaSorter
                    criteriaList={criteriaList}
                    criteriaReplies={result.data.criteria_replies}
                    noteHandle={noteHandle}
                    openTextBoxID={textBoxID}
                    setTextBoxID={setTextBoxID}
                    fileSelectorHandle={fileSelectorHandle}
                    setRecordBoxID={setRecordBoxID}
                    criteriaStateHandle={criteriaStateHandle}
                    voiceFileHandle={voiceFileHandle}
                    onlyCriteriaId={currentCriteria?.id}
                />
            )}

            <div className="rating-step-nav">
                {stepIndex > 0 && (
                    <DBButton
                        type="primary"
                        outlined
                        className="rating-step-nav-btn"
                        caption={t("prev.label")}
                        onClick={handlePrevious}
                    />
                )}
                {!isLastStep && (
                    <DBButton
                        className="rating-step-nav-btn"
                        caption={t("next.label")}
                        onClick={handleNext}
                    />
                )}
                <DBButton
                    type="primary"
                    outlined={!isLastStep}
                    className="rating-step-nav-btn"
                    caption={t("finish.label")}
                    onClick={handleFinish}
                />
            </div>
        </div>
    );
}
